import { DateRange, isNullOrUndefined, isNullOrUndefinedOrEmpty, UTC, utc } from 'rewire-common';
import { ICell, IRow } from 'rewire-grid';
import { Form, IFormContext, validator, field, IModalState } from 'rewire-ui';
import { DeepPartial, IJob, IGenerator, IGroup, ITemplate, JobInput } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import EditIcon from '@material-ui/icons/EditOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import {
  getAdvancedFormAutoComplete,
  AdvancedAutoCompleteActionType,
  AdvancedAutoCompleteSuggestionActionType,
} from '../ui/AdvancedAutoComplete';
import { confirmationModel } from '../ui/ConfirmationDialog';
import mainDateRangeNavModel from '../ui/MainDateRangeNavModel';
import { notifications } from '../ui/Notifications';
import { getNameSearcher } from '../utility/Searchers';
import setViewOnlyForFormFields from '../utility/setViewOnlyForFormFields';
import addOccupationDialogModel from './AddOccupationDialogModel';
import duplicateJobDialogModel from './DuplicateJobDialogModel';
import employeeDialogModel from './EmployeeDialogModel';
import GeneratorCalendarModel, { ITemplateCalendarEvent, IGeneratorCalendarOptions } from './GeneratorCalendarModel';
import generatorDialogModel from './GeneratorDialogModel';
import scheduleInfoPanelModel from './ScheduleInfoPanelModel';
import InfoPanelViewModelBase from './InfoPanelViewModelBase';
import scheduleModel, { jobReturnSchema, ScheduleJobCellData } from './ScheduleModel';
import updateJobEffectiveRangeDialogModel, { IUpdateJobEffectiveRangeDialogData } from './UpdateJobEffectiveRangeDialogModel';
import UdfsModel, { UdfsContainerModel } from './UdfsModel';
import duplicateJobsDialogModel from './DuplicateJobsDialogModel';

function isAllowed(values: any) {
  const { formattedValue, floatValue } = values;
  return formattedValue === '' || formattedValue === '-' || (floatValue >= -999 && floatValue <= 999);
}

interface IInfoPanelJobViewModelState extends IModalState {
  job?: IJob;
  isLoading: boolean;
}

class JobEditDialogModel extends UdfsContainerModel {
  private static _infoPanelViewModel: InfoPanelJobViewModel;
  private constructor() {
    super(
      'Job',
      async (_ids: string[]) => {
        return [];
      },
      async () => {
        return undefined;
      },
      async () => {
        return undefined;
      },
      async () => {
        return false;
      },
      'jobs',
    );
  }

  // graph the udfs right onto the job info panel
  protected _createForm(): Form {
    return JobEditDialogModel._infoPanelViewModel.form;
  }

  static create(infoPanelJobViewModel: InfoPanelJobViewModel) {
    JobEditDialogModel._infoPanelViewModel = infoPanelJobViewModel;
    return new JobEditDialogModel();
  }
}

export class InfoPanelJobViewModel extends InfoPanelViewModelBase<IInfoPanelJobViewModelState> {
  generatorCalendarModel: GeneratorCalendarModel;
  templateCalendarData?: ITemplateCalendarEvent[];
  group?: DeepPartial<IGroup>;
  position?: number;
  private jobEditDialogModel: JobEditDialogModel;

  constructor() {
    super();

    this.state.isLoading = false;

    this.generatorCalendarModel = new GeneratorCalendarModel();

    const occupationSearcher = getNameSearcher('occupations');
    const employeeSearcher = getNameSearcher('employees');
    const generatorSearcher = getNameSearcher('generators');

    const occupationAction: AdvancedAutoCompleteActionType = {
      action: this.showAddOccupationDialog,
      label: 'Add New Occupation',
      disabled: () => !mainPageModel.canAddOccupations,
    };
    const employeeAction: AdvancedAutoCompleteActionType = {
      action: this.showEmployeeDialog,
      label: 'Add New Employee',
      disabled: () => !mainPageModel.canAddEmployees,
    };
    const generatorAction: AdvancedAutoCompleteActionType = {
      action: () => this.showGeneratorDialog(),
      label: 'Add New Shift Pattern',
      disabled: () => !mainPageModel.canAddGenerators,
    };
    const generatorSuggestionAction: AdvancedAutoCompleteSuggestionActionType = {
      action: (generatorId: string) => this.showGeneratorDialog(generatorId),
      icon: mainPageModel.canEditGenerators ? EditIcon : VisibilityIcon,
    };

    this.form = Form.create(
      (form: IFormContext) => ({
        area: form.static().label('Area'),
        group: form.static().label('Group'),
        occupation: getAdvancedFormAutoComplete(form, occupationSearcher, 'Occupations', occupationAction)
          .label('Occupation')
          .validators('required'),
        autoCreateVacancyForAbsenceReasons: form.boolean().label('Auto Create Vacancy for Absence Reasons'),
        removeOverlappingAssignments: form.boolean().label('Remove Overlapping Assignments'),
        employee: getAdvancedFormAutoComplete(form, employeeSearcher, 'Employees', employeeAction).label('Assigned Employee'),
        effectiveStart: form
          .date()
          .label('Effective Start Date')
          .validators('required')
          .endAdornment(() => null as any)
          .onValueChange(this.handleEffectiveStartChange),
        effectiveEnd: form
          .date()
          .label('Effective End Date')
          .validators(validator('>=', field('effectiveStart')))
          .endAdornment(() => null as any)
          .onValueChange(this.handleEffectiveEndChange),
        generator: getAdvancedFormAutoComplete(
          form,
          generatorSearcher,
          'Shift Patterns',
          generatorAction,
          generatorSuggestionAction,
        )
          .label('Shift Pattern')
          .onValueChange(this.handleGeneratorChange),
        cycleOffset: form
          .number({ isAllowed: isAllowed })
          .label('Shift Pattern Offset')
          .validators(validator('requiredWhenOthersAreNotNull', field('generator')))
          .onValueChange(this.handleCycleOffsetChange),
      }),
      {},
    );

    this.jobEditDialogModel = JobEditDialogModel.create(this);
    setViewOnlyForFormFields(this.form, () => this.viewOnly);

    this.action('undo', this.undo, { disabled: () => this.viewOnly || (!this.hasChanges() && !!this.jobId) }).action(
      'save',
      this.submit,
      { type: 'submit', icon: 'save', disabled: () => this.viewOnly || !this.hasChanges() },
    );
  }

  reset() {
    this.form.reset();
    this.templateCalendarData = undefined;
    this.group = undefined;
    this.position = undefined;
    this.state.job = undefined;
    this.generatorCalendarModel.reset();
  }

  load = async (job?: IJob) => {
    await (this.state.isLoading = true);
    this.state.enable = false;
    if (!job) {
      this.reset();
      const area = mainPageModel.areaMap[scheduleModel.routeManager.areaId!];
      this.group = mainPageModel.groupMap[scheduleModel.routeManager.groupId!];
      this.form.value = {
        area: area?.name,
        group: this.group?.name,
        autoCreateVacancyForAbsenceReasons: true,
        removeOverlappingAssignments: true,
        effectiveStart: mainDateRangeNavModel.startDateStartOfDay,
      };
      this.generatorCalendarModel.load();
    } else {
      this.state.job = undefined;
      this.state.job = job;
      const { employee, occupation, effective, generator, group, position, fields } = this.job!;
      this.group = group;
      this.position = position;
      this.jobEditDialogModel.mapToForm(job);
      const calculatedCycleOffset = this.getAlignedCycleOffset(job, mainDateRangeNavModel.startDateStartOfDay);
      this.form.value = {
        employee: employee,
        occupation: occupation,
        area: group?.area?.name,
        group: group?.name,
        effectiveStart: mainDateRangeNavModel.startDateStartOfDay,
        effectiveEnd: effective.end,
        cycleOffset: calculatedCycleOffset,
        generator: generator,
        autoCreateVacancyForAbsenceReasons: !!fields?.['_autoCreateVacancyForAbsenceReasons:b'],
        removeOverlappingAssignments: true,
        fields: job.fields,
      };
      await this.loadGeneratorCalendar();
    }
    this.state.enable = true;
    this.state.isLoading = false;
  };

  private editor: UdfsModel;
  get udfEditor() {
    if (!this.editor) {
      this.editor = new UdfsModel('jobs');
    }
    return this.editor;
  }

  async openUdfEditor() {
    if (this.hasChanges()) {
      return;
    }
    await this.udfEditor.open();
  }

  loadGeneratorCalendar = async (displayDate?: UTC) => {
    const effectiveStart = this.form.field.effectiveStart.value;
    const effectiveEnd = this.form.field.effectiveEnd.value;
    const cycleOffset = this.form.field.cycleOffset.value;
    const generator = this.form.field.generator.value;

    this.templateCalendarData = generator?.id ? await this.getTemplateCalendarData(generator.id) : undefined;
    const generatorCalendarOptions: IGeneratorCalendarOptions = {
      calendarStartDate: effectiveStart,
      calendarEndDate: effectiveEnd,
      calendarDisplayDate: displayDate || utc(scheduleModel.routeManager.effective?.start),
      templateCalendarData: this.templateCalendarData,
      locked: true,
      offset: cycleOffset,
      repeat: true,
    };

    this.generatorCalendarModel.load(generatorCalendarOptions);
  };

  getTemplateCalendarData = async (generatorId: string): Promise<ITemplateCalendarEvent[] | undefined> => {
    const templates = await this.getTemplatesData(generatorId);
    if (templates?.length) {
      return this.generatorCalendarModel.getTemplateCalendarEvents(templates as ITemplate[]);
    }
  };

  getTemplatesData = async (generatorId: string): Promise<DeepPartial<ITemplate | undefined>[] | undefined> => {
    const returnSchema = `
      cycle {
        id
        name
        code
        fields
        template {
          start
          end
          units
          reason {
            name
          }
        }
      }
    `;

    const generators = await DataQueries.getGeneratorsByIds(returnSchema, [generatorId]);
    return generators?.[0]?.cycle;
  };

  get job(): IJob | undefined {
    return this.state.job;
  }

  get jobId(): string | undefined {
    return this.job?.id;
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  get canAddJobs(): boolean {
    return mainPageModel.canAddJobs;
  }

  get canEditJobs(): boolean {
    return mainPageModel.canEditJobs;
  }

  get canDeleteJobs(): boolean {
    return (this.canAddJobs && !this.jobId) || (mainPageModel.canDeleteJobs && !!this.jobId);
  }

  get viewOnly(): boolean {
    // existing job edits trigger new job creation, so need the add job permission as well as the edit one in order to make edits to them.
    return !((mainPageModel.canAddJobs && !this.jobId) || (mainPageModel.canAddJobs && mainPageModel.canEditJobs && !!this.jobId));
  }

  hasChanges(): boolean {
    return this.form.hasChanges;
  }

  handleEffectiveStartChange = (form: Form, v?: UTC) => {
    this.generatorCalendarModel.setCalendarStartDate(v);
    if (this.jobId && v) {
      this.form.setFieldValue('cycleOffset', this.getAlignedCycleOffset(this.job!, v));
    }
  };

  handleEffectiveEndChange = (form: Form, v?: UTC) => {
    this.generatorCalendarModel.setCalendarEndDate(v);
  };

  handleGeneratorChange = (form: Form, v?: IGenerator) => {
    if (v && isNullOrUndefinedOrEmpty(this.form.field.cycleOffset.value)) {
      this.form.field.cycleOffset.value = 0;
    }
    this.loadGeneratorCalendar(this.generatorCalendarModel.calendarDisplayDate);
  };

  handleCycleOffsetChange = (form: Form, v?: number) => {
    if (this.templateCalendarData) {
      this.generatorCalendarModel.loadCalendarEvents(this.templateCalendarData, v);
    }
  };

  getAlignedCycleOffset(job: IJob, newJobStartDate: UTC) {
    if (!job.generator || isNullOrUndefined(job.cycleOffset)) {
      return job.cycleOffset;
    }
    const baseCycleOffsetDate = job.effective.start.add(-job.cycleOffset);
    const cycleLength = job.generator.cycle.length;
    let diff = newJobStartDate.subtract(baseCycleOffsetDate);
    while (diff < 0) {
      diff += cycleLength;
    }
    return diff % cycleLength;
  }

  showGeneratorDialog = (generatorId?: string) => {
    generatorDialogModel.load(generatorId);
  };

  showEmployeeDialog = () => {
    employeeDialogModel.load();
  };

  showAddOccupationDialog = () => {
    addOccupationDialogModel.load();
  };

  showUpdateJobEffectiveRangeDialog() {
    if (!this.jobId || this.hasChanges()) {
      return;
    }
    const dialogData: IUpdateJobEffectiveRangeDialogData = {
      id: this.jobId,
      startDate: this.job!.effective.start,
      endDate: this.job!.effective.end,
    };

    updateJobEffectiveRangeDialogModel.load(dialogData);
  }

  showDuplicateJobsDialog = () => {
    if (!this.canAddJobs || !this.hasJobs() || this.hasChanges()) {
      return;
    }
    duplicateJobsDialogModel.load(this.jobs!, this.startDate);
  };

  deleteJobs = () => {
    if (!this.canDeleteJobs) return;
    if (!this.hasJobs()) {
      scheduleModel.deleteJobs();
    } else {
      scheduleModel.confirmDeleteJobs(this.jobs?.map((job) => job.id) ?? []);
    }
  };

  hasJobs() {
    return !!this.jobs?.length;
  }

  get startDate() {
    return scheduleModel.routeManager.effective?.start ?? UTC.now();
  }

  get jobs(): ScheduleJobCellData[] | undefined {
    if (!this.job) {
      return undefined;
    }
    const jobs: ScheduleJobCellData[] = [];
    for (const cell of scheduleModel.selectedCells) {
      const value = cell.value;
      if (value && typeof value.position === 'number' && value.id) {
        jobs.push(value as ScheduleJobCellData);
      }
    }

    return jobs;
  }

  showDuplicateJobDialog = () => {
    if (!this.job || this.hasChanges()) {
      return;
    }
    duplicateJobDialogModel.load(this.job);
  };

  deleteJob = () => {
    if (!this.canDeleteJobs) return;
    if (!this.jobId) {
      scheduleModel.deleteJobs();
    } else {
      scheduleModel.confirmDeleteJobs(this.jobs?.map((job) => job.id) ?? []);
    }
  };

  archiveJob() {
    if (!this.canEditJobs || !this.jobId) {
      return;
    }
    scheduleModel.archiveJob(this.jobId);
  }

  unarchiveJob() {
    if (!this.canEditJobs || !this.jobId) {
      return;
    }
    scheduleModel.unarchiveJob(this.jobId);
  }

  getConfirmationMessage(): string {
    const job = this.job!;
    const changedEffective =
      !job.effective.start.equals(this.form.field.effectiveStart.value) ||
      !job.effective.end.equals(this.form.field.effectiveEnd.value);

    let changes = '';
    if (changedEffective) {
      changes = 'create a new job';
    } else {
      changes = 'regenerate the existing job, causing all transactions to be deleted';
    }
    return `Are you sure you want to update this job? The changes will ${changes}.`;
  }

  isShallowUpdate(job: IJob): boolean {
    const field = this.form.field;
    const group = this.group;
    const effectiveStart = field.effectiveStart?.value;
    const generator = field.generator?.value;
    const employee = field.employee?.value;
    const occupation = field.occupation?.value;
    const cycleOffset = field.cycleOffset?.value;
    let adjustedCycleOffset: number | undefined;

    if (!isNullOrUndefined(job.generator) && !isNullOrUndefinedOrEmpty(cycleOffset)) {
      const cycleLength = job.generator.cycle.length;
      let diff = job.effective.start.subtract(effectiveStart) + cycleOffset;
      while (diff < 0) {
        diff += cycleLength;
      }
      adjustedCycleOffset = diff % cycleLength;
    }

    return (
      job.group?.id === group?.id &&
      job.generator?.id === generator?.id &&
      job.employee?.id === employee?.id &&
      job.occupation?.id === occupation?.id &&
      job.cycleOffset === adjustedCycleOffset
    );
  }

  undo = () => {
    if (this.viewOnly) return false;
    if (!this.jobId) {
      scheduleModel.deleteJobs();
    } else {
      this.form.revert();
      this.loadGeneratorCalendar();
    }
    return false;
  };

  private save = async (): Promise<boolean> => {
    if (this.viewOnly || !this.cell) {
      return false;
    }
    const generator = this.form.field.generator.value;
    const employee = this.form.field.employee.value;
    const occupation = this.form.field.occupation.value;
    const effective = new DateRange(this.form.field.effectiveStart.value, this.form.field.effectiveEnd.value);
    const cycleOffset = this.form.field.cycleOffset.value;
    const position = this.position || 0;
    const autoCreateVacancyForAbsenceReasons = this.form.field.autoCreateVacancyForAbsenceReasons.value ?? true;
    const removeOverlappingAssignments = this.form.field.removeOverlappingAssignments.value ?? true;
    const newJobValue = {
      groupId: this.group!.id,
      generatorId: generator?.id,
      occupationId: occupation?.id,
      employeeId: employee?.id,
      effective: effective,
      cycleOffset: cycleOffset,
      position: position,
      fields: { '_autoCreateVacancyForAbsenceReasons:b': autoCreateVacancyForAbsenceReasons },
    } as Partial<IJob>;

    const jobFields = this.jobEditDialogModel.mapToSave();
    if (jobFields.fields) Object.assign(newJobValue.fields!, jobFields.fields);

    let result: DeepPartial<IJob>[] | undefined;
    if (this.job) {
      newJobValue['id'] = this.job.id;
      result = await DataMutations.upsertJobs(jobReturnSchema, [newJobValue as unknown as JobInput], removeOverlappingAssignments);
      result = result?.map((j: IJob) => ({ ...j, prevJobId: this.job!.id }) as any);
    } else {
      newJobValue['id'] = this.cell.row.id;
      let prevJobId: string | undefined;
      let prevCell: ICell | undefined = this.cell;
      do {
        prevCell = scheduleModel.grid.adjacentTopCell(prevCell);
      } while (prevCell && !prevCell.value);

      if (this.cell.row.groupRow?.id && (!prevCell || prevCell.row.groupRow?.id !== this.cell.row.groupRow?.id)) {
        prevCell = undefined;
        let nextCell: ICell | undefined = this.cell;
        do {
          nextCell = scheduleModel.grid.adjacentBottomCell(nextCell);
        } while (nextCell && !nextCell.value);

        if (nextCell && nextCell.row.groupRow?.id === this.cell.row.groupRow?.id) {
          const jobsByJobPosition = this.cell.row.grid.rows
            .filter((r: IRow) => !scheduleModel.isNewRow(r))
            .sort((r1: IRow, r2: IRow) =>
              r1.data.job.position < r2.data.job.position ? -1 : r1.data.job.position > r2.data.job.position ? 1 : 0,
            );
          const rowAfterNewJobRowPosition = jobsByJobPosition.findIndex((jRow: IRow) => jRow.id === nextCell?.row.id);
          const rowBeforeNewJobRow = jobsByJobPosition[rowAfterNewJobRowPosition - 1];
          prevCell = scheduleModel.getJobCellFromRow(rowBeforeNewJobRow);
        }
      }

      prevJobId = prevCell?.value.id;
      result = await DataMutations.insertJobs(
        jobReturnSchema,
        [newJobValue as unknown as JobInput],
        removeOverlappingAssignments,
        prevJobId,
      );
    }

    if (result?.length) {
      await scheduleModel.updateGridJobs(result);
      notifications.success(`Job ${this.job ? 'Updated' : 'Created'}`);
      scheduleInfoPanelModel.submit();
      return true;
    }
    notifications.error(`Job ${this.job ? 'Update' : 'Creation'} Failed`);
    return false;
  };

  submit = async () => {
    if (this.job && !this.isShallowUpdate(this.job)) {
      await confirmationModel.load(this.getConfirmationMessage(), {
        yesAction: async () => {
          await this.form.submitAsync(this.save);
          return true;
        },
        maxWidth: 'sm',
        title: 'Confirm Job Update',
      });
    } else {
      await this.form.submitAsync(this.save);
    }
    return false;
  };
}

const infoPanelJobViewModel = new InfoPanelJobViewModel();
export default infoPanelJobViewModel;
